import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useOptionalConsent } from "@/components/consent/ConsentContext";
import {
  APPOINTMENT_EVENT,
  trackChatGptConversionInBrowser,
} from "@/lib/consent/chatgpt-conversion";
import { CalendarCheck, NotebookPen, Video } from "lucide-react";
import { useOnceDataLayerEvent } from "@/lib/datalayer";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ProzessTimeline } from "@/components/buergermeisterwahl/ProzessTimeline";
import { CasesSection } from "@/components/cases/CasesSection";

export const Route = createFileRoute("/termin/danke")({
  head: () => ({
    meta: [
      { title: "Vielen Dank | creategy" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Ihr Termin ist gebucht. Sie erhalten in wenigen Minuten eine Bestätigung mit Kalendereinladung per E-Mail.",
      },
      { property: "og:title", content: "Vielen Dank | creategy" },
      {
        property: "og:description",
        content:
          "Ihr Termin ist gebucht. Sie erhalten in wenigen Minuten eine Bestätigung mit Kalendereinladung per E-Mail.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/termin/danke" }],
  }),
  component: TerminDankePage,
});

const STEPS = [
  {
    title: "Termin bestätigt",
    text: "Ihre Buchung ist bei uns eingegangen, die Kalendereinladung ist auf dem Weg zu Ihnen.",
    Icon: CalendarCheck,
  },
  {
    title: "Kurze Vorbereitung",
    text: "Überlegen Sie vorab, wo Sie gerade stehen und was Sie erreichen möchten. Mehr braucht es nicht, Unterlagen sind nicht nötig.",
    Icon: NotebookPen,
  },
  {
    title: "Ihr Erstgespräch",
    text: "Simon Mai nimmt sich Zeit für Ihre Ausgangslage und zeigt Ihnen, welche nächsten Schritte sinnvoll sind. Kostenlos und unverbindlich.",
    Icon: Video,
  },
];

function TerminDankePage() {
  useOnceDataLayerEvent({ event: "terminbuchung" });
  const consent = useOptionalConsent();
  const marketing = consent?.state?.categories.marketing === true;
  useEffect(() => {
    trackChatGptConversionInBrowser(APPOINTMENT_EVENT, marketing);
  }, [marketing]);
  return (
    <Layout>
      {/* Sektion 1: Hero */}
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1 text-primary">
            Ihr Termin ist gebucht.
          </h1>
          <p className="mt-8 max-w-2xl font-body text-base leading-relaxed text-foreground md:text-lg">
            Vielen Dank für Ihre Buchung. Sie erhalten in wenigen Minuten eine
            Bestätigung mit Kalendereinladung per E-Mail. Falls Ihnen etwas
            dazwischenkommt, können Sie den Termin über den Link in dieser
            E-Mail jederzeit verschieben.
          </p>
        </div>
      </section>

      {/* Sektion 2: Prozess */}
      <section aria-labelledby="termin-danke-prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="termin-danke-prozess" className="heading-h2 text-primary">
              So geht es weiter.
            </h2>
          </Reveal>
          <div className="mt-16 md:mt-20">
            <ProzessTimeline headingColor="text-primary" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Sektion 3: Cases (nur Bereiche mit Zeeg-CTA) */}
      <CasesSection
        includeTags={["buergermeisterwahl", "direktkandidaten"]}
        title={"Cases &\u00a0Referenzen"}
        description="Ein Auszug aus Projekten, die einem Erstgespräch wie Ihrem folgten."
        headingColor="text-primary"
      />
    </Layout>
  );
}
